
import { useState } from 'react';

export default function About() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const values = [
    {
      icon: "ri-lightbulb-line",
      title: "Clarity",
      description: "Breaking down complex processes into simple steps.",
      color: "blue"
    },
    {
      icon: "ri-heart-line",
      title: "Respect",
      description: "Every client is treated with dignity.",
      color: "green"
    },
    {
      icon: "ri-eye-line",
      title: "Transparency",
      description: "We charge a small fee, but we always explain costs before we begin.",
      color: "purple"
    },
    {
      icon: "ri-shield-check-line",
      title: "Integrity",
      description: "We make clear that we are not legal advisors and do not guarantee outcomes.",
      color: "orange"
    }
  ];

  const colorClasses = {
    blue: { bg: "bg-blue-50", icon: "bg-blue-100 text-blue-600", border: "border-blue-200" },
    green: { bg: "bg-green-50", icon: "bg-green-100 text-green-600", border: "border-green-200" },
    purple: { bg: "bg-purple-50", icon: "bg-purple-100 text-purple-600", border: "border-purple-200" },
    orange: { bg: "bg-orange-50", icon: "bg-orange-100 text-orange-600", border: "border-orange-200" }
  };

  return (
    <>
      {/* Navigation */}
      <nav className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-blue-500 to-blue-700 rounded-lg shadow-lg">
                  <i className="ri-community-line text-xl text-white"></i>
                </div>
                <span className="text-2xl font-bold text-blue-600" style={{ fontFamily: '"Inter", sans-serif' }}>
                  The Help Network London
                </span>
              </div>
            </div>
            <div className="hidden md:flex space-x-8">
              <a href="/" className="text-gray-600 hover:text-blue-600 transition-colors cursor-pointer">Home</a>
              <a href="/services" className="text-gray-600 hover:text-blue-600 transition-colors cursor-pointer">Services</a>
              <a href="/about" className="text-blue-600 font-medium cursor-pointer">About</a>
              <a href="/contact" className="text-gray-600 hover:text-blue-600 transition-colors cursor-pointer">Contact</a>
            </div>
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden w-6 h-6 flex items-center justify-center cursor-pointer"
            >
              <i className={`ri-${mobileMenuOpen ? 'close' : 'menu'}-line text-xl`}></i>
            </button>
          </div>
          
          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden border-t bg-white py-4">
              <div className="flex flex-col space-y-4 px-4">
                <a href="/" className="text-gray-600 hover:text-blue-600 transition-colors cursor-pointer">Home</a>
                <a href="/services" className="text-gray-600 hover:text-blue-600 transition-colors cursor-pointer">Services</a>
                <a href="/about" className="text-blue-600 font-medium cursor-pointer">About</a>
                <a href="/contact" className="text-gray-600 hover:text-blue-600 transition-colors cursor-pointer">Contact</a>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section 
        className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 py-20 lg:py-32 overflow-hidden"
        style={{
          backgroundImage: `url('https://public.readdy.ai/ai/img_res/24ae19d002dc8a08157d098ee2f9cdf8.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900/90 to-blue-800/70"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Who We Are
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 mb-8 leading-relaxed max-w-3xl">
              The Help Network London is a community support service dedicated to helping residents navigate housing, benefits, and government systems.
            </p>
            <div className="bg-blue-800/30 backdrop-blur-sm p-6 rounded-lg border border-blue-300/20">
              <p className="text-lg text-blue-100 leading-relaxed">
                <strong className="text-white">Our mission is simple:</strong> to remove barriers and empower people with the tools and knowledge they need to succeed.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Values
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              These core principles guide everything we do and shape how we support our community.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div 
                key={index} 
                className={`${colorClasses[value.color].bg} border ${colorClasses[value.color].border} p-6 rounded-lg hover:shadow-lg transition-shadow cursor-pointer`}
              >
                <div className={`w-12 h-12 flex items-center justify-center ${colorClasses[value.color].icon} rounded-lg mb-4`}>
                  <i className={`${value.icon} text-2xl`}></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{value.title}</h3>
                <p className="text-gray-700">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why We Exist Section */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Why We Exist
            </h2>
          </div>

          <div className="bg-white p-8 rounded-lg shadow-sm border">
            <div className="prose prose-lg max-w-none">
              <p className="text-xl text-gray-700 leading-relaxed mb-6">
                Too many people fall behind because forms are confusing, letters are unclear, or English is not their first language. We exist to make sure no one is left behind.
              </p>
              
              <div className="grid md:grid-cols-3 gap-6 my-8">
                <div className="text-center">
                  <div className="w-16 h-16 flex items-center justify-center bg-red-100 rounded-full mx-auto mb-4">
                    <i className="ri-file-text-line text-3xl text-red-600"></i>
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">Confusing Forms</h3>
                  <p className="text-gray-600 text-sm">Complex government paperwork shouldn't prevent access to essential services</p>
                </div>
                
                <div className="text-center">
                  <div className="w-16 h-16 flex items-center justify-center bg-orange-100 rounded-full mx-auto mb-4">
                    <i className="ri-mail-line text-3xl text-orange-600"></i>
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">Unclear Letters</h3>
                  <p className="text-gray-600 text-sm">Official correspondence should be understandable to everyone</p>
                </div>
                
                <div className="text-center">
                  <div className="w-16 h-16 flex items-center justify-center bg-blue-100 rounded-full mx-auto mb-4">
                    <i className="ri-translate-2 text-3xl text-blue-600"></i>
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">Language Barriers</h3>
                  <p className="text-gray-600 text-sm">Everyone deserves support regardless of their first language</p>
                </div>
              </div>

              <div className="bg-blue-50 p-6 rounded-lg">
                <div className="flex items-start">
                  <div className="w-8 h-8 flex items-center justify-center bg-blue-100 rounded-full mr-4 mt-1 flex-shrink-0">
                    <i className="ri-lightbulb-line text-lg text-blue-600"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-blue-900 mb-2">Our Solution</h3>
                    <p className="text-blue-800">
                      We bridge the gap between complex government systems and the people who need them. By providing clear explanations, step-by-step guidance, and practical support, we ensure that everyone has the opportunity to access the services they're entitled to.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Impact Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Making a Difference in Our Community
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Every day, we help community residents overcome barriers and access the support they need.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-blue-100 to-blue-200 rounded-full mx-auto mb-6">
                <i className="ri-home-smile-line text-4xl text-blue-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Housing Support</h3>
              <p className="text-gray-600">Helping families understand their housing options and navigate complex application processes</p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-green-100 to-green-200 rounded-full mx-auto mb-6">
                <i className="ri-hand-heart-line text-4xl text-green-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Benefits Guidance</h3>
              <p className="text-gray-600">Supporting individuals through Universal Credit applications and benefit claims</p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-purple-100 to-purple-200 rounded-full mx-auto mb-6">
                <i className="ri-community-line text-4xl text-purple-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Community Empowerment</h3>
              <p className="text-gray-600">Building confidence and knowledge so people can advocate for themselves</p>
            </div>
          </div>
        </div>
      </section>

      {/* Disclaimer Section */}
      <section className="py-16 bg-amber-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white p-8 rounded-lg shadow-sm border border-amber-200">
            <div className="flex items-start mb-6">
              <div className="w-12 h-12 flex items-center justify-center bg-amber-100 rounded-lg mr-4 flex-shrink-0">
                <i className="ri-alert-line text-2xl text-amber-600"></i>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Important Disclaimer</h2>
              </div>
            </div>
            
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6">
                <strong>We are not legal advisors.</strong> We provide practical advice and support only. For legal advice, we will signpost you to qualified professionals or free services such as Citizens Advice.
              </p>
              
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">What This Means for You:</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span>We provide guidance and information to help you understand processes</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span>We support you in completing applications and understanding correspondence</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span>We cannot guarantee specific outcomes or make decisions on behalf of government bodies</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span>For complex legal matters, we will refer you to appropriate qualified professionals</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-indigo-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-full mx-auto mb-6">
            <i className="ri-team-line text-3xl text-white"></i>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Work with Us?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Get in touch to learn more about how we can support you through government processes with clarity and respect.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="/contact" className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-blue-50 transition-colors cursor-pointer whitespace-nowrap shadow-lg">
              Contact Us Today
            </a>
            <a href="/services" className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-blue-600 transition-colors cursor-pointer whitespace-nowrap">
              View Our Services
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-8 h-8 flex items-center justify-center bg-gradient-to-br from-blue-400 to-blue-600 rounded-lg">
                  <i className="ri-community-line text-lg text-white"></i>
                </div>
                <span className="text-2xl font-bold text-blue-400" style={{ fontFamily: '"Inter", sans-serif' }}>
                  The Help Network London
                </span>
              </div>
              <p className="text-gray-400">
                Independent guidance for government services across communities
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Services</h3>
              <ul className="space-y-2 text-gray-400">
                <li><a href="/services" className="hover:text-white transition-colors cursor-pointer">Housing Guidance</a></li>
                <li><a href="/services" className="hover:text-white transition-colors cursor-pointer">Universal Credit Support</a></li>
                <li><a href="/services" className="hover:text-white transition-colors cursor-pointer">Benefits Advice</a></li>
                <li><a href="/services" className="hover:text-white transition-colors cursor-pointer">Form Assistance</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Support</h3>
              <ul className="space-y-2 text-gray-400">
                <li><a href="/contact" className="hover:text-white transition-colors cursor-pointer">Contact</a></li>
                <li><a href="/about" className="hover:text-white transition-colors cursor-pointer">About</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">FAQs</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">Resources</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">Privacy Policy</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">Terms of Service</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">Accessibility</a></li>
                <li><a href="https://readdy.ai/?origin=logo" className="hover:text-white transition-colors cursor-pointer">Made with Readdy</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-12 pt-8 text-center text-gray-400">
            <p>&copy; 2024 The Help Network London. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </>
  );
}
